import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import {
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Wrench,
  ArrowRight,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function pad(n) { return String(n).padStart(2, '0'); }

function getMonthStr(year, month) {
  return `${year}-${pad(month + 1)}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const PERIOD_LABELS = {
  full_day: 'Full Day',
  morning: 'Morning',
  afternoon: 'Afternoon',
};

const PERIOD_COLORS = {
  full_day: 'bg-red-100 text-red-700 border-red-200',
  morning: 'bg-amber-100 text-amber-700 border-amber-200',
  afternoon: 'bg-orange-100 text-orange-700 border-orange-200',
};

export default function TechDayOffs() {
  const { selectedLocationId } = useLocationContext();
  const queryClient = useQueryClient();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const monthStr = getMonthStr(year, month);

  // Closed days: holidays and shutdowns. Lives beside technician leave because
  // both answer "why can nobody be booked then", and staff look in one place.
  const { currentLocation } = useLocationContext();
  // "All locations" in the switcher means there is no one site to close, so the
  // only honest choice there is a closure everywhere.
  const canScopeToLocation = !!selectedLocationId && currentLocation?.name !== 'All Locations';
  const [newClosure, setNewClosure] = useState({ date: '', reason: '', scope: 'location' });
  const { data: closures } = useQuery({
    // Keyed by location: the effective list is the union of global closures and
    // that site's own, so switching sites must refetch rather than reuse.
    queryKey: ['blocked-dates', selectedLocationId],
    queryFn: async () => (await axios.get('/api/blocked-dates')).data,
  });
  const blockMutation = useMutation({
    mutationFn: async (body) => (await axios.post('/api/blocked-dates', body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-dates'] });
      setNewClosure((c) => ({ date: '', reason: '', scope: c.scope }));
    },
  });
  const unblockMutation = useMutation({
    mutationFn: async ({ date, scope }) => (
      await axios.delete(`/api/blocked-dates/${date}`, { params: { scope } })
    ).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blocked-dates'] }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['day-offs', monthStr, selectedLocationId],
    queryFn: async () => {
      const { data } = await axios.get('/api/day-offs', { params: { month: monthStr } });
      return data;
    },
  });

  const addMutation = useMutation({
    mutationFn: (body) => axios.post('/api/day-offs', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['day-offs'] });
      queryClient.invalidateQueries({ queryKey: ['booked-slots'] });
      setShowAddModal(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => axios.delete(`/api/day-offs/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['day-offs'] });
    },
  });

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };
  const goToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7; // Monday-based
    const days = [];

    // Padding from previous month
    for (let i = startPad - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: formatLocalDate(d), day: d.getDate(), isCurrentMonth: false });
    }
    // Current month days
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dt = new Date(year, month, d);
      days.push({ date: formatLocalDate(dt), day: d, isCurrentMonth: true, isWeekend: dt.getDay() === 0 || dt.getDay() === 6 });
    }
    // Padding for next month
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        const dt = new Date(year, month + 1, d);
        days.push({ date: formatLocalDate(dt), day: d, isCurrentMonth: false });
      }
    }
    return days;
  }, [year, month]);

  // Map day-offs by date
  const dayOffsByDate = useMemo(() => {
    const map = {};
    if (!data?.dayOffs) return map;
    for (const d of data.dayOffs) {
      if (!map[d.date]) map[d.date] = [];
      map[d.date].push(d);
    }
    return map;
  }, [data]);

  const mechanics = data?.mechanics || [];
  const todayStr = formatLocalDate(now);
  const monthLabel = new Date(year, month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const dayOffsForSelected = selectedDate ? (dayOffsByDate[selectedDate] || []) : [];

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-brand-dark flex items-center gap-2">
                <CalendarOff className="w-5 h-5 text-red-500" />
                Leave Management
              </h1>
              <p className="text-sm text-brand-neutral mt-0.5">
                Manage technician leave — bookings are auto-reassigned
              </p>
            </div>
          </div>

          {/* Closed days */}
          <div className="bg-white rounded-xl border border-brand-light/80 p-4">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="text-sm font-bold text-brand-dark">Closed days</h2>
                <p className="text-[11px] text-brand-neutral">
                  Holidays and shutdowns. No booking can be made on these dates, by customers or staff.
                </p>
              </div>
            </div>

            <form
              className="flex flex-wrap items-end gap-2 mb-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!newClosure.date) return;
                blockMutation.mutate({
                  date: newClosure.date,
                  reason: newClosure.reason || null,
                  scope: canScopeToLocation ? newClosure.scope : 'global',
                });
              }}
            >
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-brand-neutral mb-1">Date</label>
                <input
                  type="date"
                  value={newClosure.date}
                  onChange={(e) => setNewClosure((c) => ({ ...c, date: e.target.value }))}
                  className="px-2.5 py-1.5 text-xs rounded-lg border border-brand-light focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  required
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-brand-neutral mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={newClosure.reason}
                  onChange={(e) => setNewClosure((c) => ({ ...c, reason: e.target.value }))}
                  placeholder="Eid al-Fitr, stocktake…"
                  maxLength={120}
                  className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-brand-light focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                />
              </div>
              {canScopeToLocation && (
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-brand-neutral mb-1">Applies to</label>
                  <select
                    value={newClosure.scope}
                    onChange={(e) => setNewClosure((c) => ({ ...c, scope: e.target.value }))}
                    className="px-2.5 py-1.5 text-xs rounded-lg border border-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                  >
                    <option value="location">{currentLocation?.name}</option>
                    <option value="global">All locations</option>
                  </select>
                </div>
              )}
              <button
                type="submit"
                disabled={!newClosure.date || blockMutation.isPending}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-brand-primary text-white disabled:opacity-50 hover:bg-brand-primary/90 transition-colors"
              >
                {blockMutation.isPending ? 'Blocking…' : 'Block date'}
              </button>
              {blockMutation.isError && (
                <span className="text-[11px] text-red-600">
                  {blockMutation.error?.response?.data?.error || 'Could not block that date'}
                </span>
              )}
            </form>

            {(closures?.effective || []).length === 0 ? (
              <p className="text-xs text-brand-neutral/70">No closed days set.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {closures.effective.map((c) => (
                  <span
                    key={c.date}
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg bg-red-50 border border-red-100 text-[11px]"
                    title={c.scope === 'location' ? 'Closed at this location' : 'Closed everywhere'}
                  >
                    <span className="font-semibold text-red-700">
                      {new Date(c.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    {c.reason && <span className="text-red-600/80">{c.reason}</span>}
                    <span className="text-[9px] uppercase tracking-wider text-red-400">
                      {c.scope === 'location' ? (currentLocation?.name || 'this location') : 'all locations'}
                    </span>
                    <button
                      onClick={() => unblockMutation.mutate({ date: c.date, scope: c.scope })}
                      disabled={unblockMutation.isPending}
                      className="text-red-400 hover:text-red-700 transition-colors"
                      title={`Unblock ${c.date}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Month Navigation */}
          <div className="bg-white rounded-xl border border-brand-light/80 p-4">
            <div className="flex items-center justify-between">
              <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-brand-light/60 transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-brand-dark">{monthLabel}</h2>
                <button
                  onClick={goToday}
                  className="px-3 py-1 text-[12px] font-medium text-brand-primary border border-brand-primary/30 rounded-lg hover:bg-brand-primary/5 transition-colors"
                >
                  Today
                </button>
              </div>
              <button onClick={nextMonth} className="p-2 rounded-lg hover:bg-brand-light/60 transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-xl border border-brand-light/80 overflow-hidden">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 bg-brand-light/30">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                <div key={d} className="text-center py-2 text-[11px] font-semibold text-brand-neutral uppercase tracking-wide">
                  {d}
                </div>
              ))}
            </div>
            {/* Days */}
            <div className="grid grid-cols-7 divide-x divide-y divide-brand-light/40">
              {calendarDays.map((cell, i) => {
                const offs = dayOffsByDate[cell.date] || [];
                const isToday = cell.date === todayStr;
                const isSelected = cell.date === selectedDate;
                return (
                  <button
                    key={i}
                    onClick={() => { setSelectedDate(cell.date); }}
                    className={`min-h-[80px] p-1.5 text-left transition-colors relative ${
                      !cell.isCurrentMonth ? 'bg-gray-50/50 text-gray-300' :
                      cell.isWeekend ? 'bg-gray-50/30 text-gray-400' :
                      'hover:bg-brand-light/30'
                    } ${isSelected ? 'ring-2 ring-brand-primary ring-inset' : ''}`}
                  >
                    <span className={`text-[12px] font-medium inline-flex items-center justify-center w-6 h-6 rounded-full ${
                      isToday ? 'bg-brand-primary text-white' : ''
                    }`}>
                      {cell.day}
                    </span>
                    {offs.length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {offs.slice(0, 3).map((off, j) => (
                          <div key={j} className={`text-[9px] px-1 py-0.5 rounded truncate border ${PERIOD_COLORS[off.period]}`}>
                            {off.mechanic_name.split(' ')[0]} — {PERIOD_LABELS[off.period]}
                          </div>
                        ))}
                        {offs.length > 3 && (
                          <div className="text-[9px] text-brand-neutral">+{offs.length - 3} more</div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Date Panel */}
          {selectedDate && (
            <div className="bg-white rounded-xl border border-brand-light/80 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-brand-dark">
                  {formatDate(selectedDate)}
                </h3>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Mark Day Off
                </button>
              </div>

              {dayOffsForSelected.length === 0 ? (
                <p className="text-sm text-brand-neutral py-4 text-center">No day-offs on this date. All technicians available.</p>
              ) : (
                <div className="space-y-2">
                  {dayOffsForSelected.map(off => (
                    <div key={off.id} className="flex items-center justify-between bg-red-50 rounded-lg p-3 border border-red-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                          <Wrench className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-brand-dark">{off.mechanic_name}</p>
                          <div className="flex items-center gap-2 text-[11px] text-brand-neutral">
                            <span className={`px-1.5 py-0.5 rounded border ${PERIOD_COLORS[off.period]}`}>
                              {PERIOD_LABELS[off.period]}
                            </span>
                            {off.reason && <span>{off.reason}</span>}
                            {off.created_by && <span className="opacity-60">by {off.created_by}</span>}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => { if (confirm(`Remove day-off for ${off.mechanic_name}?`)) deleteMutation.mutate(off.id); }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add Day-Off Modal */}
          {showAddModal && (
            <AddDayOffModal
              date={selectedDate}
              mechanics={mechanics}
              existingOffs={dayOffsForSelected}
              onSubmit={(body) => addMutation.mutate(body)}
              onClose={() => setShowAddModal(false)}
              isLoading={addMutation.isPending}
              result={addMutation.data?.data}
            />
          )}

          {/* Show reassignment results */}
          {addMutation.isSuccess && addMutation.data?.data?.reassignments?.length > 0 && (
            <ReassignmentSummary reassignments={addMutation.data.data.reassignments} />
          )}
        </div>
      </div>
    </Layout>
  );
}

function AddDayOffModal({ date, mechanics, existingOffs, onSubmit, onClose, isLoading }) {
  const [mechanicUid, setMechanicUid] = useState('');
  const [period, setPeriod] = useState('full_day');
  const [reason, setReason] = useState('');

  // Filter out mechanics already off for the full day
  const fullDayOffs = new Set(existingOffs.filter(o => o.period === 'full_day').map(o => o.mechanic_uid));
  const availableMechanics = mechanics.filter(m => !fullDayOffs.has(m.user_uid));

  const handleSubmit = (e) => {
    e.preventDefault();
    const mech = mechanics.find(m => m.user_uid === mechanicUid);
    if (!mech) return;
    onSubmit({
      mechanicUid: mech.user_uid,
      mechanicName: mech.name,
      date,
      period,
      reason: reason || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-brand-dark">Mark Day Off</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-sm text-brand-neutral">{formatDate(date)}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-brand-neutral uppercase tracking-wide mb-1">Technician</label>
            <select
              value={mechanicUid}
              onChange={e => setMechanicUid(e.target.value)}
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              <option value="">Select technician...</option>
              {availableMechanics.map(m => (
                <option key={m.user_uid} value={m.user_uid}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-brand-neutral uppercase tracking-wide mb-1">Period</label>
            <div className="flex gap-2">
              {['full_day', 'morning', 'afternoon'].map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    period === p
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-white text-brand-dark border-gray-200 hover:border-red-300'
                  }`}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-brand-neutral uppercase tracking-wide mb-1">Reason (optional)</label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g., Sick leave, Annual leave..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            />
          </div>

          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-100">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-[12px] text-amber-800">
              Existing bookings for this technician will be automatically reassigned to available technicians.
            </p>
          </div>

          <button
            type="submit"
            disabled={!mechanicUid || isLoading}
            className="w-full py-2.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Processing...' : 'Mark as Day Off'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ReassignmentSummary({ reassignments }) {
  if (!reassignments || reassignments.length === 0) return null;
  return (
    <div className="bg-green-50 rounded-xl border border-green-200 p-4">
      <h4 className="text-sm font-bold text-green-800 mb-2">Bookings Reassigned ({reassignments.length})</h4>
      <div className="space-y-1.5">
        {reassignments.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-[12px]">
            <span className="font-medium text-brand-dark">{r.customerName}</span>
            <span className="text-brand-neutral">({r.slotLabel})</span>
            <span className="text-red-500 line-through">{r.from}</span>
            <ArrowRight className="w-3 h-3 text-brand-neutral" />
            <span className={`font-semibold ${r.to ? 'text-green-600' : 'text-red-600'}`}>
              {r.to || 'Unassigned'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatLocalDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
